/**
 * Redux slice (src/store/slices/paymentSlice.js).
 *
 * Data flow (typical):
 * - UI dispatches actions/thunks from `src/store/actions/*`.
 * - `reducers` handles synchronous state updates.
 * - `extraReducers` reacts to thunk pending/fulfilled/rejected actions.
 */

import { createSlice } from "@reduxjs/toolkit";
import {
  fetchAllpayments,
  Add_payment,
  Updatepayment,
  Deletepayment,
  GetPaymentsByStudentID,
  Create_Payment,
  getOrderDetails,
  SearchPaymentbyStudentUsername,
  storePaymentDetails,
  fetchPaymentDetails,
  fetchAccountantDashboardPayments,
  getPayuAuthToken,
  getPayuPaymentMethods,
  manualAddWalletHours,
  downloadInfaktInvoicePDF,
  createInfaktInvoice,
  markInfaktInvoiceAsPaid,
  getPaymentStatus,
} from "../actions/paymentActions";
import { toast } from "react-toastify";
import { t } from "../../utils/i18nTranslate";

let initialState = {
  loading: false,
  paymentlist: [],
  Allpaymentlist: [],
  StudentID_Payments: [],
  paymentResponseUrl: "",
  orderId: "",
  orderStatus: "",
  storedPaymentDetails: null,
  fetchedPaymentDetails: [],
  fetchPaymentDetailsRequestId: null,
  fetchPaymentDetailsError: null,
  fetchPaymentDetailsStatus: "idle",
  accountantDashboard: {
    summary: null,
    statusData: [],
    methodData: [],
    recentPayments: [],
  },
  accountantDashboardLoading: false,
  accountantDashboardError: null,
  pagination: {
    totalPages: 1,
    totalCount: 0,
    hasNextPage: false,
    hasPrevPage: false
  },
  accounting: {
    scope: "accounting",
    cutoffStart: null,
    cutoffActive: false,
    legacyExcluded: true,
    archiveOnly: false,
  },
  payuToken: null,
  payuPaymentMethods: null,
  payuError: null,
  manualWalletResult: null,
  // PDF Download state
  downloadingInvoices: {}, // Track downloads by payment ID
  downloadError: null,
  // Invoice Creation state
  creatingInvoices: {}, // Track invoice creation by payment ID
  invoiceCreationError: null,
  // Mark Invoice as Paid state
  markingInvoicesPaid: {}, // Track marking paid by invoice ID
  markPaidError: null,
  // Payment Status state
  paymentStatus: null,
  paymentStatusLoading: false,
  paymentStatusError: null
};

/**
 * RTK slice definition: paymentSlice.
 */
const paymentSlice = createSlice({
  name: "payment",
  initialState: initialState,
  reducers: {},
  /**
   * Reducer: extraReducers.
   */
  extraReducers: (builder) => {
    builder
      // extraReducers handler: fetchAllpayments.pending
      .addCase(fetchAllpayments.pending, (state) => {
        state.loading = true;
      })
      // extraReducers handler: fetchAllpayments.fulfilled
      .addCase(fetchAllpayments.fulfilled, (state, action) => {
        const payments = Array.isArray(action.payload)
          ? action.payload
          : Array.isArray(action.payload?.payments)
            ? action.payload.payments
            : [];
        state.paymentlist = payments;
        state.Allpaymentlist = payments;
        state.accounting = {
          ...state.accounting,
          ...(action.payload?.accounting || {}),
        };
        state.loading = false;
      })
      // extraReducers handler: fetchAllpayments.rejected
      .addCase(fetchAllpayments.rejected, (state) => {
        state.loading = false;
      })
      // extraReducers handler: Add_payment.fulfilled
      .addCase(Add_payment.fulfilled, (state, action) => {
        state.Allpaymentlist.push(action.payload);
        state.loading = false;
      })
      // extraReducers handler: Add_payment.rejected
      .addCase(Add_payment.rejected, (state) => {
        state.loading = false;
      })
      // extraReducers handler: Deletepayment.fulfilled
      .addCase(Deletepayment.fulfilled, (state, action) => {
        state.loading = false;
        state.Allpaymentlist = state.Allpaymentlist.filter(
          (Payment) => Payment._id !== action.payload._id
        );
      })
      // extraReducers handler: Deletepayment.rejected
      .addCase(Deletepayment.rejected, (state) => {
        state.loading = false;
      })
      // extraReducers handler: Updatepayment.fulfilled
      .addCase(Updatepayment.fulfilled, (state, action) => {
        const { PaymentID, updatedData } = action.payload;
        const updatedPaymentIndex = state.Allpaymentlist.findIndex(
          (Payment) => Payment._id === PaymentID
        );

        if (updatedPaymentIndex !== -1) {
          const updatedPayment = {
            ...state.Allpaymentlist[updatedPaymentIndex],
            ...updatedData,
          };
          state.Allpaymentlist = [
            ...state.Allpaymentlist.slice(0, updatedPaymentIndex),
            updatedPayment,
            ...state.Allpaymentlist.slice(updatedPaymentIndex + 1),
          ];
        }
      })
      // extraReducers handler: Updatepayment.rejected
      .addCase(Updatepayment.rejected, (state) => {
        state.loading = false;
      })
      // extraReducers handler: GetPaymentsByStudentID.fulfilled
      .addCase(GetPaymentsByStudentID.fulfilled, (state, action) => {
        state.loading = false;
        state.StudentID_Payments = action.payload;
      })
      // extraReducers handler: GetPaymentsByStudentID.rejected
      .addCase(GetPaymentsByStudentID.rejected, (state) => {
        state.loading = false;
      })
      // extraReducers handler: Create_Payment.pending
      .addCase(Create_Payment.pending, (state) => {
        state.loading = true;
      })
      // extraReducers handler: Create_Payment.fulfilled
      .addCase(Create_Payment.fulfilled, (state, action) => {
        state.paymentResponseUrl = action.payload.paymentResponseUrl;
        state.orderId = action.payload.orderId;
        state.loading = false;
      })
      // extraReducers handler: Create_Payment.rejected
      .addCase(Create_Payment.rejected, (state) => {
        state.loading = false;
      })
      // extraReducers handler: getOrderDetails.pending
      .addCase(getOrderDetails.pending, (state) => {
        state.loading = true;
      })
      // extraReducers handler: getOrderDetails.fulfilled
      .addCase(getOrderDetails.fulfilled, (state, action) => {
        state.orderStatus = action.payload.status;
        state.loading = false;
      })
      // extraReducers handler: getOrderDetails.rejected
      .addCase(getOrderDetails.rejected, (state) => {
        state.loading = false;
      })
      // extraReducers handler: SearchPaymentbyStudentUsername.pending
      .addCase(SearchPaymentbyStudentUsername.pending, (state) => {
        state.loading = true;
      })
      // extraReducers handler: SearchPaymentbyStudentUsername.fulfilled
      .addCase(SearchPaymentbyStudentUsername.fulfilled, (state, action) => {
        if (action.payload && action.payload.foundedPayment) {
          state.Allpaymentlist = action.payload.foundedPayment;
        } else {
          state.Allpaymentlist = [];
        }
        state.loading = false;
      })
      // extraReducers handler: SearchPaymentbyStudentUsername.rejected
      .addCase(SearchPaymentbyStudentUsername.rejected, (state) => {
        state.loading = false;
      })
      // extraReducers handler: storePaymentDetails.pending
      .addCase(storePaymentDetails.pending, (state) => {
        state.loading = true;
      })
      // extraReducers handler: storePaymentDetails.fulfilled
      .addCase(storePaymentDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.storedPaymentDetails = action.payload.paymentDetails;
      })
      // extraReducers handler: storePaymentDetails.rejected
      .addCase(storePaymentDetails.rejected, (state) => {
        state.loading = false;
      })
      // extraReducers handler: fetchPaymentDetails.pending
      .addCase(fetchPaymentDetails.pending, (state, action) => {
        state.loading = true;
        state.fetchPaymentDetailsStatus = "loading";
        state.fetchPaymentDetailsRequestId = action.meta?.requestId || null;
        state.fetchPaymentDetailsError = null;
      })
      // extraReducers handler: fetchPaymentDetails.fulfilled
      .addCase(fetchPaymentDetails.fulfilled, (state, action) => {
        if (state.fetchPaymentDetailsRequestId && action.meta?.requestId !== state.fetchPaymentDetailsRequestId) {
          return;
        }
        if (!Array.isArray(action.payload?.paymentDetails)) {
          state.loading = false;
          state.fetchPaymentDetailsStatus = "error";
          state.fetchPaymentDetailsRequestId = null;
          state.fetchPaymentDetailsError = "Invalid payment response shape.";
          return;
        }
        state.loading = false;
        state.fetchPaymentDetailsStatus = "success";
        state.fetchPaymentDetailsRequestId = null;
        state.fetchPaymentDetailsError = null;
        state.fetchedPaymentDetails = action.payload.paymentDetails;
        state.pagination = {
          totalPages: action.payload?.totalPages ?? 1,
          totalCount: action.payload?.totalCount ?? 0,
          hasNextPage: Boolean(action.payload?.hasNextPage),
          hasPrevPage: Boolean(action.payload?.hasPrevPage)
        };
        state.accounting = {
          ...state.accounting,
          ...(action.payload?.accounting || {}),
        };
      })
      // extraReducers handler: fetchPaymentDetails.rejected
      .addCase(fetchPaymentDetails.rejected, (state, action) => {
        if (state.fetchPaymentDetailsRequestId && action.meta?.requestId !== state.fetchPaymentDetailsRequestId) {
          return;
        }
        state.loading = false;
        state.fetchPaymentDetailsStatus = "error";
        state.fetchPaymentDetailsRequestId = null;
        state.fetchPaymentDetailsError = action.error?.message || "Nie udało się pobrać płatności.";
      })
      .addCase(fetchAccountantDashboardPayments.pending, (state) => {
        state.accountantDashboardLoading = true;
        state.accountantDashboardError = null;
      })
      .addCase(fetchAccountantDashboardPayments.fulfilled, (state, action) => {
        state.accountantDashboardLoading = false;
        state.accountantDashboardError = null;
        state.accountantDashboard = {
          summary: action.payload.summary || null,
          statusData: Array.isArray(action.payload.statusData) ? action.payload.statusData : [],
          methodData: Array.isArray(action.payload.methodData) ? action.payload.methodData : [],
          recentPayments: Array.isArray(action.payload.recentPayments) ? action.payload.recentPayments : [],
        };
        state.accounting = {
          ...state.accounting,
          ...(action.payload.accounting || {}),
        };
      })
      .addCase(fetchAccountantDashboardPayments.rejected, (state, action) => {
        state.accountantDashboardLoading = false;
        state.accountantDashboardError =
          action.payload?.message ||
          action.error?.message ||
          "accountant_dashboard_unavailable";
      })
      // PayU Authorization Token cases
  .addCase(getPayuAuthToken.pending, (state) => {
    state.loading = true;
    state.payuError = null;
  })
  // extraReducers handler: getPayuAuthToken.fulfilled
  .addCase(getPayuAuthToken.fulfilled, (state, action) => {
    state.loading = false;
    state.payuToken = action.payload.token;
    state.payuError = null;
  })
  // extraReducers handler: getPayuAuthToken.rejected
  .addCase(getPayuAuthToken.rejected, (state, action) => {
    state.loading = false;
    state.payuError = action.error.message;
  })
  // PayU Payment Methods cases
  .addCase(getPayuPaymentMethods.pending, (state) => {
    state.loading = true;
    state.payuError = null;
  })
  // In your paymentSlice.js
.addCase(getPayuPaymentMethods.fulfilled, (state, action) => {
  state.loading = false;
  state.payuPaymentMethods = action.payload;  // This should contain the full response
  state.payuError = null;
})
  // extraReducers handler: getPayuPaymentMethods.rejected
  .addCase(getPayuPaymentMethods.rejected, (state, action) => {
    state.loading = false;
    state.payuError = action.error.message;
  })
  // Manual wallet hours addition cases
  .addCase(manualAddWalletHours.pending, (state) => {
    state.loading = true;
    state.payuError = null;
  })
  // extraReducers handler: manualAddWalletHours.fulfilled
  .addCase(manualAddWalletHours.fulfilled, (state, action) => {
    state.loading = false;
    state.manualWalletResult = action.payload;
    state.payuError = null;
    toast.success(action.payload.message || t("store.payment.walletHoursAdded"));
  })
  // extraReducers handler: manualAddWalletHours.rejected
  .addCase(manualAddWalletHours.rejected, (state, action) => {
    state.loading = false;
    state.payuError = action.error.message;
    toast.error(t("store.payment.walletHoursAddFailed") + ": " + action.error.message);
  })
  // Infakt PDF Download cases
  .addCase(downloadInfaktInvoicePDF.pending, (state, action) => {
    const { paymentId } = action.meta.arg;
    state.downloadingInvoices[paymentId] = true;
    state.downloadError = null;
  })
  // extraReducers handler: downloadInfaktInvoicePDF.fulfilled
  .addCase(downloadInfaktInvoicePDF.fulfilled, (state, action) => {
    const { paymentId, message } = action.payload;
    state.downloadingInvoices[paymentId] = false;
    state.downloadError = null;
    toast.success(message);
  })
  // extraReducers handler: downloadInfaktInvoicePDF.rejected
  .addCase(downloadInfaktInvoicePDF.rejected, (state, action) => {
    const { paymentId } = action.meta.arg;
    state.downloadingInvoices[paymentId] = false;
    state.downloadError = action.payload;
    toast.error(action.payload.message);
  })
  // Infakt Invoice Creation cases
  .addCase(createInfaktInvoice.pending, (state, action) => {
    const paymentId = action.meta.arg.student_id + '_' + (action.meta.arg.package_id || 'manual');
    state.creatingInvoices[paymentId] = true;
    state.invoiceCreationError = null;
  })
  // extraReducers handler: createInfaktInvoice.fulfilled
  .addCase(createInfaktInvoice.fulfilled, (state, action) => {
    const paymentId = Object.keys(state.creatingInvoices).find(key => state.creatingInvoices[key]);
    if (paymentId) {
      state.creatingInvoices[paymentId] = false;
    }
    state.invoiceCreationError = null;
    toast.success(t("store.payment.invoiceCreated"));
    // Update the payment record in fetchedPaymentDetails if it exists
    if (action.payload.payment_record) {
      const paymentIndex = state.fetchedPaymentDetails.findIndex(p => p._id === action.payload.payment_id);
      if (paymentIndex !== -1) {
        state.fetchedPaymentDetails[paymentIndex] = {
          ...state.fetchedPaymentDetails[paymentIndex],
          invoice: action.payload.payment_record.invoice
        };
      }
    }
  })
  // extraReducers handler: createInfaktInvoice.rejected
  .addCase(createInfaktInvoice.rejected, (state, action) => {
    const paymentId = Object.keys(state.creatingInvoices).find(key => state.creatingInvoices[key]);
    if (paymentId) {
      state.creatingInvoices[paymentId] = false;
    }
    state.invoiceCreationError = action.payload;
    toast.error(action.payload.message || t("store.payment.invoiceCreateFailed"));
  })
  // Mark Invoice as Paid cases
  .addCase(markInfaktInvoiceAsPaid.pending, (state, action) => {
    const invoiceId = action.meta.arg;
    state.markingInvoicesPaid[invoiceId] = true;
    state.markPaidError = null;
  })
  // extraReducers handler: markInfaktInvoiceAsPaid.fulfilled
  .addCase(markInfaktInvoiceAsPaid.fulfilled, (state, action) => {
    const invoiceId = Object.keys(state.markingInvoicesPaid).find(key => state.markingInvoicesPaid[key]);
    if (invoiceId) {
      state.markingInvoicesPaid[invoiceId] = false;
    }
    state.markPaidError = null;
    toast.success(action.payload.message || t("store.payment.invoiceMarkedPaid"));
    // Update the payment record in fetchedPaymentDetails if it exists
    if (action.payload.payment_id) {
      const paymentIndex = state.fetchedPaymentDetails.findIndex(p => p._id === action.payload.payment_id);
      if (paymentIndex !== -1) {
        state.fetchedPaymentDetails[paymentIndex] = {
          ...state.fetchedPaymentDetails[paymentIndex],
          Status: 'COMPLETED',
          invoice: {
            ...state.fetchedPaymentDetails[paymentIndex].invoice,
            status: 'paid'
          },
          fulfillment_status: 'fulfilled'
        };
      }
    }
  })
  // extraReducers handler: markInfaktInvoiceAsPaid.rejected
  .addCase(markInfaktInvoiceAsPaid.rejected, (state, action) => {
    const invoiceId = Object.keys(state.markingInvoicesPaid).find(key => state.markingInvoicesPaid[key]);
    if (invoiceId) {
      state.markingInvoicesPaid[invoiceId] = false;
    }
    state.markPaidError = action.payload;
    toast.error(action.payload.message || t("store.payment.invoiceMarkPaidFailed"));
  })
  // Get Payment Status cases
  .addCase(getPaymentStatus.pending, (state) => {
    state.paymentStatusLoading = true;
    state.paymentStatusError = null;
  })
  // extraReducers handler: getPaymentStatus.fulfilled
  .addCase(getPaymentStatus.fulfilled, (state, action) => {
    state.paymentStatusLoading = false;
    state.paymentStatus = action.payload;
    state.paymentStatusError = null;
  })
  // extraReducers handler: getPaymentStatus.rejected
  .addCase(getPaymentStatus.rejected, (state, action) => {
    state.paymentStatusLoading = false;
    state.paymentStatusError = action.error.message;
  });
  },
});

export default paymentSlice.reducer;
